import {KebabHorizontalIcon, PencilIcon, TrashIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, Label} from '@primer/react'
import {useRef} from 'react'

import {customizationTypeLabel, type McpCustomization} from '../types/customization'
import {McpServerLogo} from './McpServerLogo'
import styles from './ConnectorRow.module.css'

export interface McpServerRowProps {
  server: McpCustomization
  onEdit: (server: McpCustomization, trigger: HTMLButtonElement | null) => void
  onDelete?: (server: McpCustomization, trigger: HTMLButtonElement | null) => void
}

/**
 * One personal MCP server.
 *
 * Shares the connector row's layout so the two read as one list, with the
 * transport badge and the overflow menu marking it as the user's own. A
 * connector's row opens a detail dialog; this one edits the definition, so the
 * whole row is the edit trigger.
 */
export function McpServerRow({server, onEdit, onDelete}: McpServerRowProps) {
  const transport = customizationTypeLabel(server)
  const menuRef = useRef<HTMLButtonElement>(null)

  return (
    <div className={styles.row}>
      <button
        type="button"
        className={styles.trigger}
        onClick={event => onEdit(server, event.currentTarget)}
        aria-label={`${server.name}, ${transport} MCP server. Edit`}
      >
        <McpServerLogo transport={server.transport} />
        <span className={styles.body}>
          <span className={styles.heading}>
            <span className={styles.name}>{server.name}</span>
            <Label variant="accent">{transport}</Label>
            {server.secrets.length > 0 ? <Label variant="secondary">Secret references</Label> : null}
          </span>
          {server.description ? <span className={styles.description}>{server.description}</span> : null}
        </span>
      </button>
      <span className={styles.trailing}>
        <ActionMenu anchorRef={menuRef}>
          {/* Raised above the row overlay so the menu button stays clickable. */}
          <ActionMenu.Anchor>
            <IconButton
              icon={KebabHorizontalIcon}
              variant="invisible"
              className={styles.connect}
              aria-label={`More options for ${server.name}`}
            />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay width="small">
            <ActionList>
              <ActionList.Item onSelect={() => onEdit(server, menuRef.current)}>
                <ActionList.LeadingVisual>
                  <PencilIcon />
                </ActionList.LeadingVisual>
                Edit
              </ActionList.Item>
              {onDelete ? (
                <ActionList.Item variant="danger" onSelect={() => onDelete(server, menuRef.current)}>
                  <ActionList.LeadingVisual>
                    <TrashIcon />
                  </ActionList.LeadingVisual>
                  Remove
                </ActionList.Item>
              ) : null}
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      </span>
    </div>
  )
}
